import styled, { css } from 'styled-components';
import { getBrightInvertedColor } from '@elfsight/widget-utils';
import { clamp } from 'es-toolkit';
import { useElementSize } from '../combined-navigation';
import { useSlider } from '../SliderContext';
import { Count } from './Count';
import { Progress } from './Progress';
import type { PaginationVariantBaseProps } from './types';

const DEFAULT_MARGIN = '10px';

export interface NumbersProps extends PaginationVariantBaseProps {
  customMargin?: string;
}

export function Numbers({
  slides,
  currentIndex,
  autoplayProgressBar,
  autoplayEnabled,
  autoplayPauseOnHover,
  seconds,
  loop,
  colors,
  customMargin = DEFAULT_MARGIN,
  shouldUseNewAppearance
}: NumbersProps) {
  const [elementRef, elementSize] = useElementSize();
  const { appClassnamePrefix } = useSlider();

  const { color } = colors;
  const numberSymbolSeparator = shouldUseNewAppearance ? '' : ' ';
  const index = clamp(currentIndex + 1, 1, slides.length);

  return (
    <NumbersComponent
      _appClassPrefix={appClassnamePrefix}
      _color={color}
      _customMargin={customMargin}
      _shouldUseNewAppearance={!!shouldUseNewAppearance}
      ref={elementRef}
    >
      <Inner _appClassPrefix={appClassnamePrefix}>
        <InnerNumber
          aria-live="polite"
          aria-label={`Slide ${index} of ${slides.length}`}
          _appClassPrefix={appClassnamePrefix}
        >
          {index}
          {numberSymbolSeparator}/{numberSymbolSeparator}
          {slides.length}
        </InnerNumber>
      </Inner>

      {autoplayProgressBar && autoplayEnabled && currentIndex === index - 1 && (
        <Count
          seconds={seconds}
          autoplayProgressBar={autoplayProgressBar}
          autoplayPauseOnHover={autoplayPauseOnHover}
          active={currentIndex === index - 1}
          last={slides.length - 1 === currentIndex && !loop}
          index={currentIndex}
        >
          {({ percent }) => {
            if (shouldUseNewAppearance) {
              return (
                <Progress.Capsule
                  width={elementSize.width}
                  height={elementSize.height}
                  stroke={3}
                  percent={percent}
                  color={color}
                  appClassnamePrefix={appClassnamePrefix}
                />
              );
            }

            return (
              <Progress.Circle
                stroke={3}
                radius={33}
                percent={percent}
                color={color}
              />
            );
          }}
        </Count>
      )}
    </NumbersComponent>
  );
}

interface NumbersComponentProps {
  _appClassPrefix: string;
  _color: string;
  _customMargin: string;
  _shouldUseNewAppearance: boolean;
}

const NumbersComponent = styled.div.attrs<NumbersComponentProps>(
  ({ _appClassPrefix }) => ({
    className: `${_appClassPrefix}-numbers-component`
  })
)`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 26px;
  position: relative;

  ${({ _shouldUseNewAppearance, _customMargin }) => {
    if (_shouldUseNewAppearance) {
      return css`
        font-weight: 700;
        padding: 10px 12px;
        margin: ${_customMargin};
        box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.1);
      `;
    }

    return css`
      width: 48px;
      height: 48px;
      margin: ${DEFAULT_MARGIN};
    `;
  }}

  ${({ _color }) => css`
    background-color: ${_color};
    color: ${getBrightInvertedColor(_color, [
      'rgb(255, 255, 255)',
      'rgb(29, 33, 41)'
    ])};
  `}
`;

interface InnerProps {
  _appClassPrefix: string;
}

const Inner = styled.div.attrs<InnerProps>(({ _appClassPrefix }) => ({
  className: `${_appClassPrefix}-numbers-inner`
}))`
  font-size: 13px;
  text-align: center;
`;

interface InnerNumberProps {
  _appClassPrefix: string;
}

const InnerNumber = styled.div.attrs<InnerNumberProps>(
  ({ _appClassPrefix }) => ({
    className: `${_appClassPrefix}-numbers-number`
  })
)`
  display: flex;
`;
